import React from 'react';
import { Divider, Flex, Menu } from 'antd';
import { coreBrandColors, primitiveColors, spacing } from '../../../theming';
import classes from './LeftMenu.module.scss';
import { routesPath } from '../../constants/routesPath';
import '../../../App.scss';
import TeamSelector from '../TeamSelector/TeamSelector';
import SideMenu from '../sideMenu/SideMenu';
import { useAuth } from '../../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { checkForSuperAdmin } from '../../../utils/checkRole';
import { useImpersonationEnabled } from '../../../hooks/useImpersonationEnabled';

/** The Admin section of the menu; the Users page (impersonation, #790) only while the server has it switched on. */
export const adminMenuItems = ({ t, impersonationEnabled }) => [
    {
        key: 'admin',
        icon: (
            <span>
                <i className="fi fi-rr-user-crown" />
            </span>
        ),
        label: t('admin'),
        children: [
            {
                icon: (
                    <span>
                        <i className="fi fi-rr-list" />
                    </span>
                ),
                key: routesPath.ROOT.USERS_LIST,
                label: t('sidebar:users-list'),
            },
            impersonationEnabled && {
                icon: (
                    <span>
                        <i className="fi fi-rr-spy" />
                    </span>
                ),
                key: routesPath.ROOT.IMPERSONATION,
                label: t('sidebar:impersonation'),
            },
            {
                icon: (
                    <span>
                        <i className="fi fi-rr-envelope" />
                    </span>
                ),
                key: routesPath.ROOT.EMAIL_LOG,
                label: t('sidebar:email-log'),
            },
            {
                icon: (
                    <span>
                        <i className="fi fi-rr-barcode-read" />
                    </span>
                ),
                key: routesPath.ROOT.VIN_EXTRACTION,
                label: t('sidebar:vin-extraction'),
            },
        ].filter(Boolean),
    },
];

function LeftMenu({ isCollapsed, theme }) {
    const { currentUser } = useAuth();

    const location = useLocation();

    const navigate = useNavigate();

    const { t } = useTranslation(['sidebar']);

    const currentUserTeam = currentUser.memberships.find(memb => memb.value === currentUser.teamId);

    const onMenuClick = ({ key }) => {
        navigate(key);
    };

    const isSuperAdmin = checkForSuperAdmin(currentUser);
    const impersonationEnabled = useImpersonationEnabled(isSuperAdmin);
    const adminMenu = adminMenuItems({ t, impersonationEnabled });

    return (
        <Flex
            vertical
            style={{ backgroundColor: coreBrandColors.blueRibbon, height: '100%' }}
            className={classes['left-menu']}
        >
            {currentUserTeam && (
                <Flex justify="center">
                    <TeamSelector isCollapsed={isCollapsed} theme={theme} />
                </Flex>
            )}
            <SideMenu theme={theme} isCollapsed={isCollapsed} />
            {isSuperAdmin && (
                <Flex vertical style={{ marginTop: 'auto' }} gap={spacing[3]}>
                    <Divider
                        style={{
                            backgroundColor: primitiveColors.gray100,
                            height: 1,
                            margin: 0,
                            minWidth: 'auto',
                            maxWidth: 216,
                        }}
                    />
                    <Menu
                        onClick={onMenuClick}
                        mode="inline"
                        theme={theme}
                        defaultSelectedKeys={['1']}
                        selectedKeys={[location.pathname]}
                        defaultOpenKeys={['sub1']}
                        className={isCollapsed && classes.menu}
                        style={{
                            height: '100%',
                            borderRight: 0,
                            backgroundColor: 'inherit',
                            color:
                                theme === 'dark'
                                    ? `${primitiveColors.gray0} !important`
                                    : `${primitiveColors.gray900} !important`,
                        }}
                        overlayinnertyle={{
                            backgroundColor: theme === 'dark' ? coreBrandColors.blueRibbon : primitiveColors.gray50,
                        }}
                        items={adminMenu}
                        expandIcon={({ isSubMenu, isOpen }) => {
                            switch (true) {
                                case isSubMenu && isOpen:
                                    return <i className="fi fi-rr-angle-small-up" />;
                                case isSubMenu && !isOpen:
                                    return <i className="fi fi-rr-angle-small-down" />;
                                default:
                                    return null;
                            }
                        }}
                    />
                </Flex>
            )}
        </Flex>
    );
}

export default LeftMenu;
